/**
 * Excel Template Engine
 *
 * Opens an .xlsx template via ExcelJS, finds placeholder cells,
 * and injects artefact content. Uses the existing ExcelJS library
 * already installed in the project.
 *
 * Supported placeholders (in cell values):
 *   {{TITLE}}   — document title
 *   {{DATE}}    — export date
 *   {{AUTHOR}}  — user display name
 *   {{BRAND}}   — brand name
 *   {{CONTENT}} — artefact content (combined, plain text)
 */

import type { Artefact } from "@/types/artefacts";
import type { CapturedImage } from "@/utils/artefactExport/htmlToImage";

import { logger } from "@/telemetry";
import { ArtefactType } from "@/types/artefacts";
import { parseTableContent } from "@/utils/tableUtils";
import { stripMarkdown } from "@/utils/templateEngine/templateUtils";

// Image sizing constants for Excel embedding
const IMAGE_SCALE_DIVISOR = 2; // Captured at 2x scale for quality
const IMAGE_MAX_WIDTH = 1200; // Maximum image width in pixels
const IMAGE_MAX_HEIGHT = 800; // Maximum image height in pixels
const IMAGE_ROW_HEIGHT_APPROX = 20; // Approximate Excel row height in pixels

// Header background colour (ARGB format for ExcelJS)
const EXCEL_HEADER_BG_ARGB = "FF28326E";

export interface ExcelTemplateData {
  title?: string;
  date?: string;
  author?: string;
  brand?: string;
  artefacts: Array<{
    title: string;
    content: string;
    type: ArtefactType;
    id?: string;
  }>;
}

/**
 * Prepare template data from artefacts.
 */
export function prepareExcelTemplateData(
  artefacts: Artefact[],
  options: { title?: string; author?: string; brand?: string; locale?: string } = {},
): ExcelTemplateData {
  return {
    title: options.title || "Generated Content Export",
    date: new Date().toLocaleDateString(options.locale || "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    author: options.author || "",
    brand: options.brand || "",
    artefacts: artefacts.map((a) => ({
      title: a.title,
      content: a.type === ArtefactType.TABLE ? a.content : stripMarkdown(a.content),
      type: a.type,
      id: a.id,
    })),
  };
}

// Excel sheet name limit
const SHEET_NAME_MAX_LENGTH = 31;

/**
 * Sanitise and deduplicate a worksheet name for Excel (max 31 chars, no special chars).
 */
function sanitiseSheetName(
  title: string,
  workbook: { getWorksheet: (name: string) => unknown },
): string {
  // Remove characters invalid in Excel sheet names: \ / ? * [ ]
  let name = title.replace(/[\\/?*[\]]/g, "").trim() || "Sheet";

  name = name.slice(0, SHEET_NAME_MAX_LENGTH);

  // Ensure unique
  let finalName = name;
  let counter = 1;

  while (workbook.getWorksheet(finalName)) {
    const suffix = ` (${counter})`;

    finalName = name.slice(0, SHEET_NAME_MAX_LENGTH - suffix.length) + suffix;
    counter++;
  }

  return finalName;
}

/**
 * Write a single artefact (table or HTML) into a worksheet starting at startRow.
 * Returns the next available row after the content.
 */
function writeArtefactToSheet(
  workbook: any,
  worksheet: any,
  artefact: { title: string; content: string; type: ArtefactType; id?: string },
  imageMap?: Map<string, CapturedImage>,
  startRow: number = 1,
): number {
  let currentRow = startRow;

  if (artefact.type === ArtefactType.TABLE) {
    const table = parseTableContent(artefact.content);

    if (!table || !table.headers || !table.rows) {
      // Fallback: write title + raw content
      worksheet.getCell(currentRow, 1).value = artefact.title;
      worksheet.getCell(currentRow, 1).font = { bold: true, size: 14 };
      currentRow++;
      worksheet.getCell(currentRow, 1).value = artefact.content;
      currentRow++;

      return currentRow;
    }

    // Title row
    worksheet.getCell(currentRow, 1).value = artefact.title;
    worksheet.getCell(currentRow, 1).font = { bold: true, size: 14 };
    currentRow++;

    // Header row with styling
    const headerRow = worksheet.getRow(currentRow);

    headerRow.values = table.headers;
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: EXCEL_HEADER_BG_ARGB },
    };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    currentRow++;

    // Data rows
    for (const row of table.rows) {
      const excelRow = worksheet.getRow(currentRow);
      const values = table.headers.map((h) => row[h] || "");

      excelRow.values = values;
      currentRow++;
    }

    // Auto-size columns
    table.headers.forEach((header: string, colIndex: number) => {
      const column = worksheet.getColumn(colIndex + 1);
      const maxLength = Math.max(
        header.length,
        ...table.rows.map((row: Record<string, string>) => (row[header] || "").toString().length),
      );

      column.width = Math.min(maxLength + 2, 50);
    });

    // Add auto-filter
    worksheet.autoFilter = {
      from: { row: currentRow - table.rows.length - 1, column: 1 },
      to: { row: currentRow - 1, column: table.headers.length },
    };

    return currentRow;
  }

  // HTML artefact — embed image if available
  worksheet.getCell(currentRow, 1).value = artefact.title;
  worksheet.getCell(currentRow, 1).font = { bold: true, size: 14 };
  currentRow++;

  const image = artefact.id && imageMap ? imageMap.get(artefact.id) : undefined;

  if (image) {
    try {
      const base64Data = image.base64.replace(/^data:image\/\w+;base64,/, "");
      const imageId = workbook.addImage({
        base64: base64Data,
        extension: "png",
      });

      let width = image.width / IMAGE_SCALE_DIVISOR;
      let height = image.height / IMAGE_SCALE_DIVISOR;

      if (width > IMAGE_MAX_WIDTH) {
        const ratio = IMAGE_MAX_WIDTH / width;

        width = IMAGE_MAX_WIDTH;
        height = height * ratio;
      }

      if (height > IMAGE_MAX_HEIGHT) {
        const ratio = IMAGE_MAX_HEIGHT / height;

        height = IMAGE_MAX_HEIGHT;
        width = width * ratio;
      }

      worksheet.addImage(imageId, {
        tl: { col: 0, row: currentRow - 1 },
        ext: { width, height },
        editAs: "oneCell",
      });

      const rowsSpanned = Math.ceil(height / IMAGE_ROW_HEIGHT_APPROX);

      currentRow += rowsSpanned + 1;
    } catch (error) {
      logger.error("Failed to add HTML artefact image to Excel template", {
        error: String(error),
      });
      worksheet.getCell(currentRow, 1).value = `Chart: ${artefact.title} (could not be embedded)`;
      worksheet.getCell(currentRow, 1).font = { italic: true, color: { argb: "FFFF6B00" } };
      currentRow++;
    }
  } else {
    worksheet.getCell(currentRow, 1).value = `Chart: ${artefact.title} (image not available)`;
    worksheet.getCell(currentRow, 1).font = { italic: true, color: { argb: "FFFF6B00" } };
    currentRow++;
  }

  return currentRow;
}

/**
 * Apply an Excel template to artefact data.
 *
 * @param templateBuffer - The .xlsx template file as ArrayBuffer
 * @param data - Template data with placeholders to fill
 * @param imageMap - Optional map of captured HTML artefact images
 * @param options - Export options (e.g. useSeparateWorksheets)
 * @returns Blob of the rendered .xlsx file
 */
export async function applyExcelTemplate(
  templateBuffer: ArrayBuffer,
  data: ExcelTemplateData,
  imageMap?: Map<string, CapturedImage>,
  options?: { useSeparateWorksheets?: boolean },
): Promise<Blob> {
  try {
    // Dynamic import to keep ExcelJS out of the main bundle
    const ExcelJS = await import("exceljs");
    const workbook = new ExcelJS.Workbook();

    await workbook.xlsx.load(templateBuffer);

    // Separate artefacts by type
    const tableArtefacts = data.artefacts.filter((a) => a.type === ArtefactType.TABLE);
    const htmlArtefacts = data.artefacts.filter((a) => a.type === ArtefactType.HTML);
    const textArtefacts = data.artefacts.filter(
      (a) => a.type !== ArtefactType.TABLE && a.type !== ArtefactType.HTML,
    );

    // Build replacement map (text artefacts only — tables and HTML get structured output)
    const replacements: Record<string, string> = {
      "{{TITLE}}": data.title || "",
      "{{DATE}}": data.date || "",
      "{{AUTHOR}}": data.author || "",
      "{{BRAND}}": data.brand || "",
      "{{CONTENT}}": textArtefacts.map((a) => `${a.title}\n${a.content}`).join("\n\n"),
    };

    // Walk through all cells in all worksheets and replace placeholders
    workbook.eachSheet((worksheet) => {
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          if (typeof cell.value === "string") {
            let value = cell.value;

            for (const [placeholder, replacement] of Object.entries(replacements)) {
              if (value.includes(placeholder)) {
                value = value.replace(placeholder, replacement);
              }
            }

            // Prevent Excel formula injection: prefix suspicious values with a single quote
            if (/^[=+\-@]/.test(value)) {
              cell.value = `'${value}`;
            } else {
              cell.value = value;
            }
          }
        });
      });
    });

    // Collect content artefacts (tables + HTML) that need worksheet output
    const contentArtefacts = [...tableArtefacts, ...htmlArtefacts];
    const useSeparateWorksheets = options?.useSeparateWorksheets ?? false;

    if (contentArtefacts.length > 0) {
      if (useSeparateWorksheets) {
        // Create one worksheet per artefact
        for (const artefact of contentArtefacts) {
          const sheetName = sanitiseSheetName(artefact.title, workbook);
          const worksheet = workbook.addWorksheet(sheetName);

          writeArtefactToSheet(workbook, worksheet, artefact, imageMap);
        }
      } else {
        // Write all content into a single worksheet
        let contentSheet = workbook.getWorksheet("Exported Content");

        if (!contentSheet) {
          contentSheet = workbook.getWorksheet("Content");
        }
        if (!contentSheet) {
          contentSheet = workbook.addWorksheet("Content");
        }

        let currentRow = 1;

        // Find the next available row (after any existing content)
        contentSheet.eachRow((_, rowNumber) => {
          currentRow = rowNumber + 1;
        });

        if (currentRow > 1) {
          currentRow += 1;
        }

        for (const artefact of contentArtefacts) {
          currentRow = writeArtefactToSheet(workbook, contentSheet, artefact, imageMap, currentRow);
          currentRow += 2; // Spacing between artefacts
        }
      }
    }

    // Generate output
    const buffer = await workbook.xlsx.writeBuffer();

    logger.info("Excel template applied successfully", {
      artefactCount: data.artefacts.length,
      title: data.title,
    });

    return new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  } catch (error) {
    logger.error("Failed to apply Excel template", { error: String(error) });
    throw new Error(
      `Failed to apply Excel template: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}
